import { ApiFailure, TokenStore, createApi, unwrap, type Api } from '../api/client';
import {
  createAccount,
  deriveCredentials,
  formatRecoveryKey,
  kdfFromServer,
  unlockKeyring,
  verifyMasterPassword,
  type Keyring,
} from '../crypto/account';
import { CryptoError, fromBase64, wipe, toBase64, type KdfParams } from '../crypto/sodium';

export type AuthStatus = 'signed-out' | 'busy' | 'signed-in';

export interface AuthState {
  status: AuthStatus;
  userId?: string;
  email?: string;
  /** Espace personnel : celui où Notes range ses Items. */
  personalSpaceId?: string;
  error?: { code: string; message: string; /** Délai avant de pouvoir réessayer (`RATE_LIMITED`, `TOO_MANY_ATTEMPTS`), pour le décompte. */ retryAfterSeconds?: number };
  /** Recovery Key à montrer une seule fois après l'inscription. */
  recoveryKey?: string;
}

/** Résultat de la vérification du mot de passe maître (déverrouillage d'un service). */
export type VerifyResult = { ok: true } | { ok: false; reason: 'wrong' } | { ok: false; reason: 'wait'; waitSeconds: number };

export interface Session {
  userId: string;
  email: string;
  personalSpaceId: string;
  keyring: Keyring;
  api: Api;
  /** Vérifie le mot de passe maître sans nouvelle connexion (déverrouillage de Vault) ; ralentit après des échecs. */
  verifyPassword: (password: string) => Promise<VerifyResult>;
}

const normalizeEmail = (email: string): string => email.trim().toLowerCase();

/** Messages sans détail exploitable par un attaquant : même texte pour e-mail inconnu et mauvais mot de passe. */
function describe(error: unknown): NonNullable<AuthState['error']> {
  if (error instanceof ApiFailure) {
    if (error.code === 'INVALID_CREDENTIALS') return { code: error.code, message: 'E-mail ou mot de passe incorrect.' };
    // `RATE_LIMITED` : trop d'appels (prelogin) ; `TOO_MANY_ATTEMPTS` : verrou après des échecs de connexion.
    if (error.code === 'RATE_LIMITED' || error.code === 'TOO_MANY_ATTEMPTS') {
      const wait = error.retryAfterSeconds ? ` Réessayez dans ${error.retryAfterSeconds} s.` : '';
      return { code: error.code, message: `Trop de tentatives.${wait}`, ...(error.retryAfterSeconds ? { retryAfterSeconds: error.retryAfterSeconds } : {}) };
    }
    if (error.code === 'EMAIL_ALREADY_REGISTERED') return { code: error.code, message: 'Un compte existe déjà avec cet e-mail.' };
    return { code: error.code, message: error.message };
  }
  if (error instanceof CryptoError) return { code: 'CRYPTO', message: 'E-mail ou mot de passe incorrect.' };
  return { code: 'NETWORK', message: 'Le serveur est injoignable. Vérifiez votre connexion.' };
}

/**
 * Inscription, connexion et déconnexion. Le mot de passe maître ne quitte jamais l'appareil : le serveur ne
 * reçoit que l'`auth_key` dérivée et les clés chiffrées. Jetons et clés restent en mémoire (CLAUDE.md §8).
 */
export class AuthService {
  private state: AuthState = { status: 'signed-out' };
  private session: Session | null = null;
  /** Ce qu'il faut pour revérifier le mot de passe maître : paramètres Argon2id et clé de compte chiffrée. */
  private verifier: { kdf: KdfParams; wrappedAccountKey: string } | null = null;
  private failedVerifications = 0;
  private nextVerificationAt = 0;
  private readonly listeners = new Set<() => void>();
  readonly tokens = new TokenStore();
  readonly api: Api;

  private readonly newKdf: (() => KdfParams) | undefined;

  /** `newKdf` : coûts Argon2id d'un nouveau compte (réduits dans les tests ; calibrés par appareil en production). */
  constructor(options: { baseFetch?: typeof fetch; baseUrl?: string; newKdf?: () => KdfParams } = {}) {
    this.newKdf = options.newKdf;
    this.api = createApi({ tokens: this.tokens, baseFetch: options.baseFetch, baseUrl: options.baseUrl, onSessionLost: () => this.logoutLocal('SESSION_LOST') });
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getState = (): AuthState => this.state;
  getSession = (): Session | null => this.session;

  private set(state: AuthState): void {
    this.state = state;
    this.listeners.forEach((l) => l());
  }

  async register(email: string, password: string, deviceName = 'Navigateur web'): Promise<boolean> {
    this.set({ status: 'busy' });
    try {
      const mail = normalizeEmail(email);
      const material = await createAccount(password, this.newKdf?.());
      const result = unwrap(
        await this.api.POST('/api/v1/auth/register', {
          body: {
            email: mail,
            ...material.body,
            kdfParams: material.body.kdfParams as never,
            device: { name: deviceName, platform: 'WEB' },
          },
        }),
      );
      this.tokens.set(result.accessToken ?? '', result.refreshToken ?? '');
      this.verifier = { kdf: kdfFromServer(material.body.kdfParams), wrappedAccountKey: material.body.wrappedAccountKey };
      this.open(result.user?.id ?? '', mail, material.spaceId, material.keyring);
      this.set({ ...this.state, recoveryKey: formatRecoveryKey(material.recoveryKey) });
      wipe(material.recoveryKey);
      return true;
    } catch (error) {
      this.set({ status: 'signed-out', error: describe(error) });
      return false;
    }
  }

  async login(email: string, password: string, deviceName = 'Navigateur web'): Promise<boolean> {
    this.set({ status: 'busy' });
    try {
      const mail = normalizeEmail(email);
      const { kdfParams } = unwrap(await this.api.POST('/api/v1/auth/prelogin', { body: { email: mail } }));
      const kdf = kdfFromServer(kdfParams);
      const { kek, authKey } = await deriveCredentials(password, kdf);
      const result = unwrap(
        await this.api.POST('/api/v1/auth/login', {
          body: { email: mail, authKey: toBase64(authKey), device: { name: deviceName, platform: 'WEB' } },
        }),
      );
      wipe(authKey);
      const user = result.user;
      const spaces = (result.spaces ?? []).map((s) => ({ id: s.id ?? '', wrappedSpaceKey: s.wrappedSpaceKey ?? '' }));
      const keyring = unlockKeyring(
        kek,
        {
          publicKey: user?.publicKey ?? '',
          encPrivateKey: user?.encPrivateKey ?? '',
          wrappedAccountKey: user?.wrappedAccountKey ?? '',
        },
        spaces,
      );
      wipe(kek);
      this.tokens.set(result.accessToken ?? '', result.refreshToken ?? '');
      const personal = (result.spaces ?? []).find((s) => s.kind === 'PERSONAL')?.id ?? spaces[0]?.id ?? '';
      this.verifier = { kdf, wrappedAccountKey: user?.wrappedAccountKey ?? '' };
      this.open(user?.id ?? '', mail, personal, keyring);
      return true;
    } catch (error) {
      this.tokens.clear();
      this.set({ status: 'signed-out', error: describe(error) });
      return false;
    }
  }

  private open(userId: string, email: string, personalSpaceId: string, keyring: Keyring): void {
    this.failedVerifications = 0;
    this.nextVerificationAt = 0;
    this.session = { userId, email, personalSpaceId, keyring, api: this.api, verifyPassword: (password) => this.verifyPassword(password) };
    this.set({ status: 'signed-in', userId, email, personalSpaceId });
  }

  /**
   * Vérifie le mot de passe maître localement. Après trois échecs, chaque nouvel essai est retardé (5 s, 10 s… 60 s max) :
   * la vitesse d'essai ne dépend plus seulement du coût d'Argon2id.
   */
  private async verifyPassword(password: string): Promise<VerifyResult> {
    const wait = Math.ceil((this.nextVerificationAt - Date.now()) / 1000);
    if (wait > 0) return { ok: false, reason: 'wait', waitSeconds: wait };
    const verifier = this.verifier;
    if (!verifier) return { ok: false, reason: 'wrong' };
    const ok = await verifyMasterPassword(password, verifier.kdf, verifier.wrappedAccountKey).catch(() => false);
    if (ok) {
      this.failedVerifications = 0;
      this.nextVerificationAt = 0;
      return { ok: true };
    }
    this.failedVerifications += 1;
    if (this.failedVerifications >= 3) this.nextVerificationAt = Date.now() + Math.min(60, 5 * 2 ** (this.failedVerifications - 3)) * 1000;
    return { ok: false, reason: 'wrong' };
  }

  /** La Recovery Key a été montrée : on ne la garde plus. */
  acknowledgeRecoveryKey(): void {
    const { recoveryKey: _shown, ...rest } = this.state;
    this.set(rest);
  }

  async logout(): Promise<void> {
    try {
      if (this.tokens.access) await this.api.POST('/api/v1/auth/logout');
    } catch {
      // déconnexion locale quoi qu'il arrive
    }
    this.logoutLocal();
  }

  private logoutLocal(reason?: string): void {
    this.session?.keyring.wipe();
    this.session = null;
    this.verifier = null;
    this.tokens.clear();
    this.set({ status: 'signed-out', ...(reason ? { error: { code: reason, message: 'Votre session a expiré. Reconnectez-vous.' } } : {}) });
  }
}

export { fromBase64 };
